import { Box, Checkbox, MenuItem, TextField, Typography } from "@material-ui/core";
import { ArrowDropDown } from "@material-ui/icons";
import React, { Dispatch, Fragment, SetStateAction, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import LyfeTooltip from "../../../../components/LyfeTooltip";
import ModalDialog from "../../../../components/ModalDialog";
import { SelectableValue } from "../../../../models/common/selectable-value";
import { ASSIGN_USER_TO_DOMAIN, GET_USER_CREATED_SIMULATIONS_COUNT } from "../queries";
import { GetUserCreatedSimulationsCountQuery, GetUserDomainsTreeQuery } from "../types/queries.generated";
import useOrganizationStyles from "../use-organization-styles";
import { DomainLevel } from "apps/platform-client/client/src/models/enums/enums";
import SearchMenuItem from "../../../simulator/details/components/SearchMenuItem";
import { globalDomainId } from "@lyfegen-platform/graphql-schema/models";
import { sanitizeSelectedDomains } from "apps/platform-client/client/src/models/domains/helpers/functions";
import { useLazyModelsQuery, useModelsMutation } from "apps/platform-client/client/src/hooks/useModelsModule";

export interface DomainItem {
	id: string;
	name: string;
	level: DomainLevel;
	children?: Array<DomainItem>;
}

interface IDomainSelectProps {
	userId: string;
	values: string[];
	email?: string | null;
	domains: Array<SelectableValue>;
	domainOptionsTree: GetUserDomainsTreeQuery["GetUserDomainsTree"];
	setUsersWithNonSelectedDomains: Dispatch<SetStateAction<string[]>>;
}

// Flatten all domains from nested structure into a flat array
export const getAllDomains = (items: Array<DomainItem>): Array<DomainItem> => {
	const allDomains: Array<DomainItem> = [];
	items.forEach((item) => {
		allDomains.push(item);
		if (item.children) {
			allDomains.push(...getAllDomains(item.children));
		}
	});
	return allDomains;
};

// Filter domains based on search text - only filter at SUBDOMAIN level, always show full parent tree
export const filterDomainsBySearch = (items: Array<DomainItem>, searchText: string): Array<DomainItem> => {
	if (!searchText.trim()) {
		return items;
	}

	const filtered: Array<DomainItem> = [];
	const searchLower = searchText.toLowerCase().trim();

	items.forEach((item) => {
		if (item.level === DomainLevel.GLOBAL || item.level === DomainLevel.PRODUCT) {
			// Filter children recursively
			const filteredChildren = item.children ? filterDomainsBySearch(item.children, searchText) : [];

			// Include parent if it has any matching SUBDOMAIN children
			if (filteredChildren.length > 0) {
				filtered.push({
					...item,
					children: filteredChildren,
				});
			}
		} else if (item.level === DomainLevel.SUBDOMAIN) {
			// Only filter at SUBDOMAIN level (lowest level)
			const itemNameLower = item.name.toLowerCase();
			if (itemNameLower.includes(searchLower)) {
				filtered.push(item);
			}
		}
	});

	return filtered;
};

export const DomainMultiSelect = (props: IDomainSelectProps) => {
	const { domains, values, userId, email, setUsersWithNonSelectedDomains } = props;
	const { t } = useTranslation();
	const { menuItemContent, textAlignLeft } = useOrganizationStyles();
	const [showPromptCount, setShowPromptCount] = useState<number>(0);
	const [selectedDomains, setSelectedDomains] = useState<string[] | null>(null);
	const [selectedDomainIds, setSelectedDomainIds] = useState<Set<string>>(new Set());
	const [showValidationError, setShowValidationError] = useState(false);
	const [menuOpen, setMenuOpen] = useState(false);
	const [searchText, setSearchText] = useState("");
	const previousValueRef = useRef<string | undefined>(undefined);
	const isInitialMount = useRef(true);
	const hasSingleDomain = domains.length <= 1;
	const [mutate, { error: domainAssignError }] = useModelsMutation(ASSIGN_USER_TO_DOMAIN, {
		context: { module: "users" },
		onError: (error) => {
			console.error("Error assigning user to domain:", error);
			setIsDomainErrorModalOpen(true);
		},
		refetchQueries: ["GetGeneralConfigurationWithDomains"],
	});

	const [isDomainErrorModalOpen, setIsDomainErrorModalOpen] = useState(false);

	useEffect(() => {
		if (showValidationError) {
			setUsersWithNonSelectedDomains((prev) => (prev.includes(userId) ? prev : [...prev, userId]));
		} else {
			setUsersWithNonSelectedDomains((prev) => prev.filter((id) => id !== userId));
		}
	}, [showValidationError, userId, setUsersWithNonSelectedDomains]);

	const [getUserSimulationCount, { loading: checkingSimulations }] = useLazyModelsQuery<GetUserCreatedSimulationsCountQuery>(
		GET_USER_CREATED_SIMULATIONS_COUNT,
		{
			context: { module: "users" },
			onError: (error) => {
				console.error("Error getting user simulation count:", error);
				setSelectedDomains(null);
			},
		}
	);

	const handleDomainSelection = () => {
		// Check that input does not include parent and its child at the same time
		const cleanedValues = sanitizeSelectedDomains({ domainTree: props.domainOptionsTree, selectedIds: Array.from(selectedDomainIds) });
		setSelectedDomains(cleanedValues);
		if (cleanedValues.length === values.length && cleanedValues.every((id) => values.includes(id))) {
			return;
		}

		const unselectedDomains = values.filter((id) => !cleanedValues.includes(id) && !cleanedValues.includes(globalDomainId));
		if (cleanedValues.length < 1) {
			return;
		}

		if (unselectedDomains.length && email) {
			getUserSimulationCount({
				variables: {
					getUserCreatedSimulationsCountArgsDto: {
						userEmail: email,
						domainIds: unselectedDomains,
					},
				},
				onCompleted: (data) => {
					if (data.GetUserCreatedSimulationsCount?.count && data.GetUserCreatedSimulationsCount?.count > 0) {
						setShowPromptCount(data.GetUserCreatedSimulationsCount.count);
					} else {
						assignUserToDomain(cleanedValues);
					}
				},
			});
		} else {
			assignUserToDomain(cleanedValues);
		}
	};

	const assignUserToDomain = (domainIds: string[]) => {
		mutate({
			variables: {
				input: { userId, domainIds },
			},
		});
	};

	const confirmReassigning = () => {
		if (selectedDomains?.length) {
			assignUserToDomain(selectedDomains);
			setShowPromptCount(0);
			setSelectedDomains(null);
		}
	};

	const cancelReassigning = () => {
		setShowPromptCount(0);
		setSelectedDomains(null);
		const initialSelected = getInitialValues();
		setSelectedDomainIds(initialSelected);
	};

	const dialogText = {
		question: t("reassign-user-prompt-title"),
		reason: t("reassign-user-prompt-description", { simulationsCount: showPromptCount }),
		continue: t("re-assign"),
		discard: t("no-cancel"),
	};

	const domainErrorDialogText = {
		question: t("simulator-reassign-user-domain-error-p1"),
		reason: t("simulator-reassign-user-domain-error-p2"),
		continue: t("try-again"),
		discard: t("no-cancel"),
	};

	if (hasSingleDomain && domains[0]) {
		return <Typography variant="body2">{domains[0].label}</Typography>;
	}

	const getPaddingBasedOnDomainLevel = (domainLevel: DomainLevel, highestAvailableDomain: DomainLevel): string => {
		const domainLevelDiff = domainLevel - highestAvailableDomain;
		switch (domainLevelDiff) {
			case DomainLevel.GLOBAL:
				return "";
			case DomainLevel.PRODUCT:
				return "37px";
			case DomainLevel.SUBDOMAIN:
				return "55px";
			default:
				return "";
		}
	};

	// Get all child domain IDs recursively
	const getAllChildDomainIds = (domain: DomainItem): string[] => {
		const childIds: string[] = [];
		if (domain.children) {
			domain.children.forEach((child) => {
				childIds.push(child.id);
				childIds.push(...getAllChildDomainIds(child));
			});
		}

		return childIds;
	};

	// Flatten all domains from nested structure into a flat array
	const getAllDomains = (items: Array<DomainItem>): Array<DomainItem> => {
		const allDomains: Array<DomainItem> = [];
		items.forEach((item) => {
			allDomains.push(item);
			if (item.children) {
				allDomains.push(...getAllDomains(item.children));
			}
		});

		return allDomains;
	};

	// Find domain by ID
	const getDomainById = (domainId: string): DomainItem | undefined => {
		const allDomains = getAllDomains(props.domainOptionsTree as Array<DomainItem>);

		return allDomains.find((d) => d.id === domainId);
	};

	// Find domain label by ID
	const getDomainLabelById = (domainId: string): string => {
		const domain = getDomainById(domainId);

		return domain?.name || "";
	};

	const getInitialValues = () => {
		const allDomains = getAllDomains(props.domainOptionsTree as Array<DomainItem>);
		const initialSelected = new Set<string>();

		values.forEach((value) => {
			const domain = allDomains.find((d) => d.id === value);
			if (!domain) {
				return;
			}

			// Always select the domain itself
			initialSelected.add(domain.id);

			// If GLOBAL or PRODUCT, also select all children
			if ((domain.level === DomainLevel.GLOBAL || domain.level === DomainLevel.PRODUCT) && domain.children) {
				const childIds = getAllChildDomainIds(domain);
				childIds.forEach((id) => initialSelected.add(id));
			}
		});

		return initialSelected;
	};

	// Initialize selectedDomainIds with the value prop if it exists
	// Only sync when value changes externally (not from user checkbox interactions)
	useEffect(() => {
		// Always initialize on first mount, then only update if value actually changed (external change)
		const serializedValues = JSON.stringify(values);
		if (isInitialMount.current || serializedValues !== previousValueRef.current) {
			isInitialMount.current = false;
			previousValueRef.current = serializedValues;

			if (values.length) {
				const initialSelected = getInitialValues();
				setSelectedDomainIds(initialSelected);
			} else {
				// Clear selections if no values
				setSelectedDomainIds(new Set());
			}
		}
	}, [values]);

	// Get display text for selected domains
	const getSelectedDomainsDisplay = (): string => {
		// If validation error is shown and no domains are selected, show error message
		if (showValidationError && selectedDomainIds.size === 0) {
			return t("simulator-domain-user-assignation");
		}

		if (selectedDomainIds.size === 0) {
			// Only show value if there's no validation error (meaning it's a valid initial value)
			if (!showValidationError && values[0]) {
				return getDomainLabelById(values[0]);
			}
			// If no selection and validation error, show error message
			return t("simulator-domain-user-assignation");
		}

		const selectedIdsArray = sanitizeSelectedDomains({
			domainTree: props.domainOptionsTree,
			selectedIds: Array.from(selectedDomainIds),
		});
		const firstSelectedId = selectedIdsArray[0];
		const firstLabel = getDomainLabelById(firstSelectedId);
		const additionalCount = selectedIdsArray.length - 1;

		if (additionalCount > 0) {
			return `${firstLabel} + ${additionalCount}`;
		}

		return firstLabel;
	};

	const buildParentMap = (
		items: Array<DomainItem>,
		parentMap: Map<string, DomainItem> = new Map(),
		parent: DomainItem | null = null
	): Map<string, DomainItem> => {
		items.forEach((item) => {
			if (parent) {
				parentMap.set(item.id, parent);
			}
			if (item.children) {
				buildParentMap(item.children, parentMap, item);
			}
		});

		return parentMap;
	};

	// Check if a domain should be disabled based on its parent selection
	const shouldDisableDomain = (domain: DomainItem, allDomains: Array<DomainItem>): boolean => {
		if (domain.level === DomainLevel.GLOBAL) {
			return false;
		}

		const parentMap = buildParentMap(allDomains);
		let currentDomain: DomainItem | undefined = domain;

		// Traverse up the parent chain to find if any parent is selected
		while (currentDomain) {
			const parent = parentMap.get(currentDomain.id);
			if (parent && selectedDomainIds.has(parent.id)) {
				// If domain is PRODUCT, disable if parent is GLOBAL
				if (domain.level === DomainLevel.PRODUCT && parent.level === DomainLevel.GLOBAL) {
					return true;
				}
				// If domain is SUBDOMAIN, disable if parent is GLOBAL or PRODUCT
				if (
					domain.level === DomainLevel.SUBDOMAIN &&
					(parent.level === DomainLevel.GLOBAL || parent.level === DomainLevel.PRODUCT)
				) {
					return true;
				}
			}
			currentDomain = parent;
		}

		return false;
	};

	const handleCheckboxChange = (domain: DomainItem, event: React.MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
		const isCurrentlySelected = selectedDomainIds.has(domain.id);

		if (isCurrentlySelected) {
			// Uncheck: Remove domain and all its children
			const newSelected = new Set(selectedDomainIds);
			newSelected.delete(domain.id);
			const childIds = getAllChildDomainIds(domain);
			childIds.forEach((id) => newSelected.delete(id));
			setSelectedDomainIds(newSelected);
			// Clear validation error if any selection remains
			if (newSelected.size > 0) {
				setShowValidationError(false);
			}
		} else {
			// Check: Add domain
			const newSelected = new Set(selectedDomainIds);
			newSelected.add(domain.id);

			// If it's a GLOBAL or PRODUCT domain, also select all children
			if ((domain.level === DomainLevel.GLOBAL || domain.level === DomainLevel.PRODUCT) && domain.children) {
				const childIds = getAllChildDomainIds(domain);
				childIds.forEach((id) => newSelected.add(id));
			}

			setSelectedDomainIds(newSelected);
			// Clear validation error when a domain is selected
			setShowValidationError(false);
		}
	};

	const renderDomainMenuItems = (domainItems: Array<DomainItem>, allDomains: Array<DomainItem> = domainItems): JSX.Element[] => {
		const items: JSX.Element[] = [];

		domainItems.forEach((domain) => {
			const isSelected = selectedDomainIds.has(domain.id);
			const isDisabled = shouldDisableDomain(domain, allDomains);

			const menuItemContentElement = (
				<>
					<Checkbox
						checked={isSelected}
						indeterminate={false}
						style={{ padding: "0 9px 0 0" }}
						disabled={isDisabled}
						onClick={(e) => {
							e.stopPropagation();
							if (!isDisabled) {
								handleCheckboxChange(domain, e);
							}
						}}
					/>
					<Box key={domain.id} className={menuItemContent}>
						<Typography variant="body2">{domain.name}</Typography>
					</Box>
				</>
			);

			items.push(
				<MenuItem
					key={domain.id}
					value={domain.id}
					style={{ paddingLeft: getPaddingBasedOnDomainLevel(domain.level, props.domainOptionsTree[0].level) }}
					disabled={isDisabled}
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						if (!isDisabled) {
							handleCheckboxChange(domain, e as any);
						}
					}}
				>
					{isDisabled ? (
						<LyfeTooltip title={t("simulator-domain-user-assignation-dropdown-disabled")}>
							<Box style={{ pointerEvents: "all", display: "flex", alignItems: "center", width: "100%" }}>
								{menuItemContentElement}
							</Box>
						</LyfeTooltip>
					) : (
						menuItemContentElement
					)}
				</MenuItem>
			);

			if (domain.children && domain.children.length > 0) {
				items.push(...renderDomainMenuItems(domain.children, allDomains));
			}
		});

		return items;
	};

	return (
		<Fragment>
			<TextField
				variant="standard"
				select
				value={values}
				style={{ borderBottom: showValidationError && selectedDomainIds.size === 0 ? "1px solid #ED544D" : "none" }}
				SelectProps={{
					open: menuOpen,
					onOpen: () => {
						setMenuOpen(true);
						setShowValidationError(false);
					},
					renderValue: () => {
						return (
							<Box className={menuItemContent} width={"199px"}>
								<Typography
									variant="body2"
									className={textAlignLeft}
									style={showValidationError && selectedDomainIds.size === 0 ? { color: "#ED544D" } : undefined}
								>
									{getSelectedDomainsDisplay()}
								</Typography>
							</Box>
						);
					},
					IconComponent: (props) => (
						<ArrowDropDown
							{...props}
							style={{ color: showValidationError && selectedDomainIds.size === 0 ? "#ED544D" : undefined }}
						/>
					),
					MenuProps: {
						// Prevent MUI from automatically focusing the first MenuItem
						// when the menu opens (focus is managed manually / by SearchMenuItem)
						MenuListProps: {
							onKeyDown: (event: React.KeyboardEvent) => {
								if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
									event.preventDefault();
									event.stopPropagation();
								}
							},
						},

						disableAutoFocusItem: true,
						PaperProps: {
							style: {
								borderTop: "solid 2px #367BF5",
								borderRadius: "5px",
								maxHeight: "460px",
							},
						},
						onClose: (event: React.SyntheticEvent | {}, reason: string) => {
							if (reason === "backdropClick") {
								setMenuOpen(false);
								if (selectedDomainIds.size === 0) {
									setShowValidationError(true);
								} else {
									handleDomainSelection();
								}
							}
						},
					},
					native: false,
				}}
				InputProps={{ disableUnderline: true }}
				error={showValidationError}
				disabled={checkingSimulations}
			>
				<SearchMenuItem
					options={getAllDomains(props.domainOptionsTree as Array<DomainItem>)}
					searchText={searchText}
					setSearchText={setSearchText}
				/>
				{renderDomainMenuItems(
					filterDomainsBySearch(props.domainOptionsTree as Array<DomainItem>, searchText),
					props.domainOptionsTree as Array<DomainItem>
				)}
			</TextField>
			{!!domainAssignError && (
				<ModalDialog
					isOpen={isDomainErrorModalOpen}
					onContinueClick={() => handleDomainSelection()}
					onDiscardClick={() => setIsDomainErrorModalOpen(false)}
					text={domainErrorDialogText}
					isFixedWidth
				/>
			)}
			{showPromptCount > 0 && (
				<ModalDialog
					isOpen={showPromptCount > 0}
					onContinueClick={confirmReassigning}
					onDiscardClick={cancelReassigning}
					text={dialogText}
				/>
			)}
		</Fragment>
	);
};
